// client/src/components/AdminStudents.js
// Students management page for admins and superadmins

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { arrayUnion, doc, getDoc, updateDoc } from 'firebase/firestore';
import { isSuperAdmin } from '../utils/roleUtils';
import { apiFetch } from '../services/apiClient';
import { getAdminStudentStatsUrl } from '../utils/urlUtils';
import AnalyticsReportingService from '../services/analyticsReportingService';
import { HeadlineMedium, LabelMedium, BodyMedium } from './Typography';
import { fetchAccessibleOrganizations, validateUserAction } from '../utils/orgDataUtils';
import { exportStudentsToCsv } from '../utils/csvExport';
import Toast from './Toast';

/** Compact table date: m/d/yy h:mm am/pm */
const formatCompactDate = (value) => {
  if (!value) return 'Never';

  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';

    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = String(date.getFullYear()).slice(-2);
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    hours = hours % 12 || 12;

    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
  } catch (err) {
    return 'Never';
  }
};

/** Longer date format for CSV export */
const formatExportDate = (value) => {
  if (!value) return 'Never';

  try {
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return 'Never';

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (err) {
    return 'Never';
  }
};

const AdminStudents = ({ organizationId }) => {
  const { userRole, userOrganizations } = useAuth();
  const [organizationNames, setOrganizationNames] = useState({});
  const [allOrganizationIds, setAllOrganizationIds] = useState([]);
  const [students, setStudents] = useState([]);
  const [userStatsById, setUserStatsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statsError, setStatsError] = useState(null);

  // Promote student → admin: confirmation target and in-flight uid
  const [promoteConfirmStudent, setPromoteConfirmStudent] = useState(null);
  const [promotingUid, setPromotingUid] = useState(null);
  const promoteDialogTitleRef = useRef(null);
  const loadRequestRef = useRef(0);

  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  /** Shows a toast message (success or error). */
  const showToast = useCallback((message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }, []);

  /** Hides the toast. */
  const hideToast = useCallback(() => {
    setToastVisible(false);
  }, []);

  /** Closes the promote-to-admin confirmation dialog without saving. */
  const closePromoteModal = useCallback(() => {
    setPromoteConfirmStudent(null);
  }, []);

  // Move focus into the dialog when it opens (keyboard / screen reader entry point)
  useEffect(() => {
    if (promoteConfirmStudent && promoteDialogTitleRef.current) {
      promoteDialogTitleRef.current.focus();
    }
  }, [promoteConfirmStudent]);

  // Close promote dialog on Escape when not submitting
  useEffect(() => {
    if (!promoteConfirmStudent) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !promotingUid) {
        closePromoteModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [promoteConfirmStudent, promotingUid, closePromoteModal]);

  // Load organization names and IDs via admin API (avoids unscoped Firestore reads)
  const loadOrganizationNames = useCallback(async () => {
    try {
      const orgs = await fetchAccessibleOrganizations(userRole, userOrganizations || []);
      const names = {};
      const orgIds = [];

      orgs.forEach((org) => {
        names[org.id] = org.name || org.organizationName || org.title || 'Unknown Organization';
        orgIds.push(org.id);
      });

      setOrganizationNames(names);
      setAllOrganizationIds(orgIds);
    } catch (err) {
      console.error('Could not load organization names:', err.message);
      const fallbackNames = {};
      const fallbackIds = userOrganizations || [];
      fallbackIds.forEach((orgId) => {
        fallbackNames[orgId] = orgId;
      });
      setOrganizationNames(fallbackNames);
      setAllOrganizationIds(fallbackIds);
    }
  }, [userRole, userOrganizations]);

  // Load students for selected organization via server API (Firestore client
  // queries on users/ are not permitted for regular admins).
  const loadStudents = useCallback(async (orgId) => {
    if (!orgId) {
      setStudents([]);
      setUserStatsById({});
      setLoading(false);
      return;
    }

    const requestId = ++loadRequestRef.current;

    try {
      setLoading(true);
      setStatsLoading(true);
      setError(null);
      setStatsError(null);

      const userStats = await AnalyticsReportingService.getUserActivitySummary(orgId);

      if (requestId !== loadRequestRef.current) {
        return;
      }

      const statsMap = {};
      const studentList = [];

      userStats.forEach((row) => {
        const role = row.role || 'student';
        if (role === 'admin' || role === 'superadmin') {
          return;
        }

        statsMap[row.userId] = row;
        studentList.push({
          id: row.userId,
          email: row.email || '',
          display_name: row.displayName || row.display_name || ''
        });
      });

      studentList.sort((a, b) => a.email.localeCompare(b.email));
      setStudents(studentList);
      setUserStatsById(statsMap);
    } catch (err) {
      if (requestId !== loadRequestRef.current) {
        return;
      }
      console.error('Error loading students:', err);
      setError('Failed to load students. Please try again.');
      setStudents([]);
      setUserStatsById({});
    } finally {
      if (requestId === loadRequestRef.current) {
        setLoading(false);
        setStatsLoading(false);
      }
    }
  }, []);

  const availableOrganizations = isSuperAdmin(userRole) ? allOrganizationIds : (userOrganizations || []);

  useEffect(() => {
    loadOrganizationNames();
  }, [loadOrganizationNames]);

  useEffect(() => {
    setStudents([]);
    setUserStatsById({});
    setError(null);
    if (organizationId) {
      loadStudents(organizationId);
    } else {
      setLoading(false);
    }
  }, [organizationId, loadStudents]);

  /** Re-sort students when stats arrive so last-login order is accurate. */
  const sortedStudents = React.useMemo(() => {
    return [...students].sort((a, b) => {
      const aStats = userStatsById[a.id];
      const bStats = userStatsById[b.id];
      const aLogin = aStats?.lastLogin ? new Date(aStats.lastLogin).getTime() : 0;
      const bLogin = bStats?.lastLogin ? new Date(bStats.lastLogin).getTime() : 0;

      if (aLogin !== bLogin) return bLogin - aLogin;
      return a.email.localeCompare(b.email);
    });
  }, [students, userStatsById]);

  const orgDisplayName = organizationNames[organizationId] || organizationId;

  /** Download the current student table (with stats) as a CSV file. */
  const handleExportCsv = useCallback(() => {
    if (sortedStudents.length === 0) {
      showToast('No students to export.', 'error');
      return;
    }

    if (statsLoading) {
      showToast('Statistics are still loading. Try again in a moment.', 'error');
      return;
    }

    try {
      exportStudentsToCsv({
        students: sortedStudents,
        userStatsById,
        organizationName: orgDisplayName,
        formatDisplayDate: formatExportDate
      });
      showToast(`Exported ${sortedStudents.length} students to CSV.`, 'success');
    } catch (err) {
      console.error('CSV export failed:', err);
      showToast('Failed to export CSV. Please try again.', 'error');
    }
  }, [sortedStudents, userStatsById, orgDisplayName, statsLoading, showToast]);

  const handleFullDataExport = useCallback(async () => {
    if (!organizationId) return;
    try {
      const data = await apiFetch(`/api/admin/export/${organizationId}`, { authenticated: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `patron-data-${organizationId}-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
      showToast('Full patron data exported.', 'success');
    } catch (err) {
      console.error('Full export failed:', err);
      showToast('Failed to export patron data.', 'error');
    }
  }, [organizationId, showToast]);

  /**
   * Promotes the selected student to admin for the currently selected organization.
   */
  const handleConfirmPromoteToAdmin = useCallback(async () => {
    if (!promoteConfirmStudent || !organizationId) return;

    if (!isSuperAdmin(userRole)) {
      showToast('Only superadmins can promote students to administrator.', 'error');
      closePromoteModal();
      return;
    }

    const { id: targetUid, email } = promoteConfirmStudent;
    setPromotingUid(targetUid);

    try {
      const userRef = doc(db, 'users', targetUid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        showToast('User no longer exists. Refresh the page.', 'error');
        closePromoteModal();
        return;
      }

      const userData = snap.data();
      const targetUser = { id: targetUid, ...userData };

      if (targetUser.role === 'admin' || targetUser.role === 'superadmin') {
        showToast('This account is already an administrator.', 'error');
        closePromoteModal();
        await loadStudents(organizationId);
        return;
      }

      if (!validateUserAction(userRole, userOrganizations || [], targetUser, 'edit')) {
        showToast('You do not have permission to change this account.', 'error');
        return;
      }

      const orgIds = targetUser.organizationIds || [];

      await apiFetch('/api/admin/change-role', {
        method: 'POST',
        authenticated: true,
        body: JSON.stringify({
          userId: targetUid,
          organizationId: organizationId,
          newRole: 'admin'
        })
      });

      if (!orgIds.includes(organizationId)) {
        const userRef = doc(db, 'users', targetUid);
        await updateDoc(userRef, {
          organizationIds: arrayUnion(organizationId),
          currentOrganizationId: organizationId,
          updatedAt: new Date()
        });
      }

      showToast(`${email} is now an administrator for this organization.`, 'success');
      closePromoteModal();
      await loadStudents(organizationId);
    } catch (err) {
      console.error('Promote to admin failed:', err);
      showToast('Failed to update this account. Please try again.', 'error');
    } finally {
      setPromotingUid(null);
    }
  }, [
    promoteConfirmStudent,
    organizationId,
    userRole,
    userOrganizations,
    loadStudents,
    closePromoteModal,
    showToast
  ]);

  const canManagePromotions = isSuperAdmin(userRole);
  const columnCount = 7;

  if (!availableOrganizations || availableOrganizations.length === 0) {
    return (
      <div className="text-center py-12">
        <HeadlineMedium color="gray-900" className="mb-4">
          No Organizations Available
        </HeadlineMedium>
        <BodyMedium color="gray-600">You need to be a member of an organization to view students.</BodyMedium>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Toast message={toastMessage} type={toastType} isVisible={toastVisible} onClose={hideToast} duration={4000} />

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <HeadlineMedium color="gray-900">
            {isSuperAdmin(userRole) ? `Students - ${orgDisplayName}` : 'Students'}
          </HeadlineMedium>
          <div className="flex flex-wrap items-center gap-3">
            {!isSuperAdmin(userRole) && organizationId && (
              <BodyMedium color="gray-600">{orgDisplayName}</BodyMedium>
            )}
            <button
              type="button"
              onClick={handleFullDataExport}
              disabled={loading || !organizationId}
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              aria-label="Export full patron data including writing samples"
            >
              Export Full Data
            </button>
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={loading || statsLoading || sortedStudents.length === 0}
              className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={`Export ${sortedStudents.length} students to CSV`}
            >
              Export CSV
            </button>
          </div>
        </div>

        {statsLoading && !loading && (
          <BodyMedium color="gray-500" className="mb-4">Loading statistics…</BodyMedium>
        )}
        {statsError && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4" role="status">
            <BodyMedium color="gray-800">{statsError}</BodyMedium>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-hidden />
            <span className="ml-3 text-gray-600">Loading students...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-4" role="alert">
            <BodyMedium color="red-800">{error}</BodyMedium>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th scope="col" className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Email Address</LabelMedium>
                  </th>
                  <th scope="col" className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Last Login</LabelMedium>
                  </th>
                  <th scope="col" className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Logins</LabelMedium>
                  </th>
                  <th scope="col" className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Courses</LabelMedium>
                  </th>
                  <th scope="col" className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Lessons</LabelMedium>
                  </th>
                  <th scope="col" className="text-left py-3 px-4">
                    <LabelMedium color="gray-600">Prompts</LabelMedium>
                  </th>
                  <th scope="col" className="text-right py-3 px-4">
                    <LabelMedium color="gray-600">Actions</LabelMedium>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map((student) => {
                  const stats = userStatsById[student.id];

                  return (
                    <tr key={student.id} className="border-b border-gray-100">
                      <td className="py-2 px-4 max-w-[220px]">
                        <BodyMedium color="gray-900" className="break-all">
                          {student.email}
                        </BodyMedium>
                      </td>
                      <td className="py-2 px-4 whitespace-nowrap">
                        <BodyMedium color="gray-900">
                          {stats ? formatCompactDate(stats.lastLogin) : (statsLoading ? '…' : '—')}
                        </BodyMedium>
                      </td>
                      <td className="py-2 px-4">
                        <BodyMedium color="gray-900">
                          {stats ? stats.logins ?? 0 : (statsLoading ? '…' : '—')}
                        </BodyMedium>
                      </td>
                      <td className="py-2 px-4">
                        <BodyMedium color="gray-900">
                          {stats
                            ? AnalyticsReportingService.formatAccessCounts(stats.courses)
                            : (statsLoading ? '…' : '—')}
                        </BodyMedium>
                      </td>
                      <td className="py-2 px-4">
                        <BodyMedium color="gray-900">
                          {stats
                            ? AnalyticsReportingService.formatAccessCounts(stats.lessons)
                            : (statsLoading ? '…' : '—')}
                        </BodyMedium>
                      </td>
                      <td className="py-2 px-4">
                        <BodyMedium color="gray-900">
                          {stats
                            ? AnalyticsReportingService.formatAccessCounts(stats.prompts)
                            : (statsLoading ? '…' : '—')}
                        </BodyMedium>
                      </td>
                      <td className="py-2 px-4 text-right">
                        <div className="flex flex-wrap justify-end gap-1">
                          <Link
                            to={getAdminStudentStatsUrl(student.id, organizationId)}
                            className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                            aria-label={`View stats for ${student.email}`}
                            title="View stats"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </Link>
                          {canManagePromotions && (
                            <button
                              type="button"
                              onClick={() => setPromoteConfirmStudent(student)}
                              disabled={!!promotingUid}
                              className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded"
                              aria-label={`Make ${student.email} an administrator for ${orgDisplayName}`}
                              title="Make admin"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {sortedStudents.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="py-8 text-center">
                      <BodyMedium color="gray-500">No students found</BodyMedium>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <BodyMedium color="gray-600">
              Total Students: <span className="font-semibold text-gray-900">{students.length}</span>
            </BodyMedium>
          </div>
        )}
      </div>

      {promoteConfirmStudent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => {
            if (!promotingUid) closePromoteModal();
          }}
          role="presentation"
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="promote-student-dialog-title"
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="promote-student-dialog-title"
              tabIndex={-1}
              ref={promoteDialogTitleRef}
              className="text-lg font-semibold text-gray-900 mb-3 outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
            >
              Make administrator?
            </h2>
            <BodyMedium color="gray-700" className="mb-6">
              {promoteConfirmStudent.display_name ? (
                <>
                  <span className="font-medium text-gray-900">{promoteConfirmStudent.display_name}</span> (
                  {promoteConfirmStudent.email}) will become an <strong>administrator</strong> for{' '}
                  <strong>{orgDisplayName}</strong>.
                </>
              ) : (
                <>
                  <span className="font-medium text-gray-900">{promoteConfirmStudent.email}</span> will become an{' '}
                  <strong>administrator</strong> for <strong>{orgDisplayName}</strong>.
                </>
              )}
            </BodyMedium>
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={closePromoteModal}
                disabled={!!promotingUid}
                className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmPromoteToAdmin}
                disabled={!!promotingUid}
                className="inline-flex items-center justify-center min-h-[44px] px-4 rounded-md border border-transparent bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {promotingUid === promoteConfirmStudent.id ? 'Saving…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudents;
